import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts';
import AnalyticsApiService, {
  PostObservabilityOverviewResponse,
  PostObservabilityPostDetail,
  PostObservabilityPostRow,
} from '../../services/analyticsApi';

type RangePreset = '1h' | '6h' | '24h' | '7d';
type Bucket = '5m' | '15m' | '1h' | '1d';
type ObservabilityTab = 'overview' | 'waves' | 'posts' | 'detail';

const RANGE_PRESETS: Array<{ value: RangePreset; label: string }> = [
  { value: '1h', label: 'Last 1h' },
  { value: '6h', label: 'Last 6h' },
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
];

const BUCKETS: Array<{ value: Bucket; label: string }> = [
  { value: '5m', label: '5m' },
  { value: '15m', label: '15m' },
  { value: '1h', label: '1h' },
  { value: '1d', label: '1d' },
];

const SORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'recent', label: 'Recency' },
  { value: 'eq', label: 'EQ' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'accepts', label: 'Accepts' },
  { value: 'repliesCount', label: 'Replies' },
];

function formatNumber(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '—';
  return value.toLocaleString();
}

function computeRange(range: RangePreset): { from: string; to: string } {
  const end = new Date();
  const to = end.toISOString();
  const start = new Date(end);
  if (range === '1h') start.setHours(start.getHours() - 1);
  if (range === '6h') start.setHours(start.getHours() - 6);
  if (range === '24h') start.setHours(start.getHours() - 24);
  if (range === '7d') start.setDate(start.getDate() - 7);
  return { from: start.toISOString(), to };
}

function formatShortDateTime(iso: string, timeZone: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone,
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

function formatEtTimestamp(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
}

function funnelValue(overview: PostObservabilityOverviewResponse | null, stage: string): number {
  if (!overview) return 0;
  const item = overview.funnel?.find((f) => f.stage === stage);
  return item?.value ?? 0;
}

function MiniChartTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border border-gray-200 dark:border-gray-800 rounded-xl p-3 shadow-xl">
      <p className="font-['SF_Pro_Text',_-apple-system,_sans-serif] font-semibold text-gray-900 dark:text-gray-100 text-xs mb-2">
        {label}
      </p>
      {payload.map((entry: any) => (
        <p key={entry.dataKey ?? entry.name} className="font-['SF_Pro_Text',_-apple-system,_sans-serif] text-sm text-gray-700 dark:text-gray-300">
          <span className="font-semibold" style={{ color: entry.color }}>
            {entry.name ?? entry.dataKey}:
          </span>{' '}
          {typeof entry.value === 'number' ? entry.value.toLocaleString() : String(entry.value)}
        </p>
      ))}
    </div>
  );
}

export function PostObservabilityPanel({ className }: { className?: string }) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<ObservabilityTab>('overview');
  const [range, setRange] = useState<RangePreset>('24h');
  const [bucket, setBucket] = useState<Bucket>('1h');
  const [sort, setSort] = useState<string>('eq');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  const [overview, setOverview] = useState<PostObservabilityOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState<boolean>(true);
  const [overviewError, setOverviewError] = useState<string | null>(null);

  const [posts, setPosts] = useState<PostObservabilityPostRow[]>([]);
  const [postsTotal, setPostsTotal] = useState<number>(0);
  const [postsLoading, setPostsLoading] = useState<boolean>(true);
  const [postsError, setPostsError] = useState<string | null>(null);

  const [selectedPostId, setSelectedPostId] = useState<string>('');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [postIdSearch, setPostIdSearch] = useState<string>('');
  const [detail, setDetail] = useState<PostObservabilityPostDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const { from, to } = useMemo(() => computeRange(range), [range]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil((postsTotal || 0) / pageSize)), [postsTotal, pageSize]);
  const showingFrom = postsTotal === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = postsTotal === 0 ? 0 : Math.min(page * pageSize, postsTotal);
  const windowLabels = useMemo(() => {
    return {
      utc: `UTC: ${formatShortDateTime(from, 'UTC')} → ${formatShortDateTime(to, 'UTC')}`,
      et: `ET: ${formatShortDateTime(from, 'America/New_York')} → ${formatShortDateTime(to, 'America/New_York')}`,
    };
  }, [from, to]);

  // Reset paging when key inputs change (matches series-post-service dashboard behavior)
  useEffect(() => {
    setPage(1);
  }, [range, sort, pageSize]);

  // Fetch overview
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setOverviewLoading(true);
      setOverviewError(null);
      try {
        const data = await AnalyticsApiService.getPostObservabilityOverview({ from, to, bucket });
        if (cancelled) return;
        setOverview(data);
      } catch (e: any) {
        if (cancelled) return;
        setOverviewError(e?.message || 'Failed to load overview');
        setOverview(null);
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [from, to, bucket]);

  // Fetch posts list
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setPostsLoading(true);
      setPostsError(null);
      try {
        const data = await AnalyticsApiService.getPostObservabilityPosts({
          from,
          to,
          sort,
          page,
          page_size: pageSize,
        });
        if (cancelled) return;
        setPosts(data.results || []);
        setPostsTotal(data.total || 0);
      } catch (e: any) {
        if (cancelled) return;
        setPostsError(e?.message || 'Failed to load posts');
        setPosts([]);
        setPostsTotal(0);
      } finally {
        if (!cancelled) setPostsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [from, to, sort, page, pageSize]);

  // Default selection to first row
  useEffect(() => {
    if (posts.length === 0) return;
    if (!selectedPostId || !posts.some((p) => p.id === selectedPostId)) {
      setSelectedPostId(posts[0].id);
    }
  }, [posts, selectedPostId]);

  useEffect(() => {
    setCopyState('idle');
  }, [selectedPostId]);

  // Fetch detail for selected post id
  useEffect(() => {
    const id = selectedPostId?.trim();
    if (!id) return;
    const idNum = Number(id);
    if (!Number.isFinite(idNum) || idNum <= 0) return;

    let cancelled = false;
    const run = async () => {
      setDetailLoading(true);
      setDetailError(null);
      try {
        const data = await AnalyticsApiService.getPostObservabilityPostDetail(idNum, { from, to });
        if (cancelled) return;
        setDetail(data);
      } catch (e: any) {
        if (cancelled) return;
        setDetailError(e?.message || 'Failed to load post detail');
        setDetail(null);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedPostId, from, to]);

  const handleSearchById = () => {
    const raw = postIdSearch.trim();
    if (!raw) return;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) {
      setDetailError('Enter a valid numeric Post ID');
      return;
    }
    setSelectedPostId(String(Math.trunc(n)));
    setActiveTab('detail');
  };

  const handleCopySelectedPostId = async () => {
    const text = selectedPostId.trim();
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    } finally {
      window.setTimeout(() => setCopyState('idle'), 1200);
    }
  };

  const impressions = funnelValue(overview, 'Impressions');
  const reads = funnelValue(overview, 'Reads');
  const replies = funnelValue(overview, 'Replies');
  const accepts = funnelValue(overview, 'Accepts');

  const repliesSection = detail ? (
    <div>
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
        Replies ({detail.replies?.length ?? 0})
      </div>
      <div className="space-y-2 max-h-72 overflow-auto">
        {(detail.replies ?? []).map((r) => (
          <div key={r.id} className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
            <div className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{r.text}</div>
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              User: {r.user} {r.ts ? `• ${r.ts}` : ''}
            </div>
          </div>
        ))}
        {(detail.replies ?? []).length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">No replies in this window.</div>
        ) : null}
      </div>
    </div>
  ) : null;

  const recipientsSection = detail ? (
    <div>
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">Recipients</div>
      <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 overflow-hidden">
        <Table className="text-sm">
          <TableHeader className="bg-gray-50/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300">
            <TableRow className="border-gray-200/60 dark:border-gray-800/60">
              <TableHead className="px-3 py-2 text-left font-semibold">User</TableHead>
              <TableHead className="px-3 py-2 text-left font-semibold">Status</TableHead>
              <TableHead className="px-3 py-2 text-left font-semibold">Read latency</TableHead>
              <TableHead className="px-3 py-2 text-right font-semibold">Replies</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(detail.users ?? []).map((u) => (
              <TableRow key={`${u.id}`} className="border-gray-200/60 dark:border-gray-800/60">
                <TableCell className="px-3 py-2 text-gray-900 dark:text-gray-100">{u.id}</TableCell>
                <TableCell className="px-3 py-2 text-gray-700 dark:text-gray-300">{u.status}</TableCell>
                <TableCell className="px-3 py-2 text-gray-700 dark:text-gray-300">{u.readLatency}</TableCell>
                <TableCell className="px-3 py-2 text-right text-gray-900 dark:text-gray-100">{formatNumber(u.replies ?? 0)}</TableCell>
              </TableRow>
            ))}
            {(detail.users ?? []).length === 0 ? (
              <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                <TableCell colSpan={4} className="px-3 py-3 text-gray-500 dark:text-gray-400">
                  No recipients in this window.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  ) : null;

  return (
    <div
      className={`bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50 shadow-xl overflow-hidden ${className || ''}`}
    >
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        aria-controls="post-observability-content"
        aria-label="Toggle Post Observability panel"
        className="w-full px-8 py-6 flex items-start justify-between hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </div>

          <div className="flex-1">
            <h2 className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-900 dark:text-gray-100 text-left">
              Post Observability
            </h2>
            <p className="text-sm font-['SF_Pro_Text',_-apple-system,_sans-serif] text-gray-600 dark:text-gray-400 text-left">
              Post-service engagement funnel + post drilldowns
            </p>

            {!overviewLoading && overview ? (
              <div className="flex flex-wrap items-center gap-6 mt-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Impressions</p>
                  <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-gray-800 dark:text-gray-200">
                    {formatNumber(impressions)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reads</p>
                  <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-sky-600 dark:text-sky-400">
                    {formatNumber(reads)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Replies</p>
                  <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-emerald-600 dark:text-emerald-400">
                    {formatNumber(replies)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Accepts</p>
                  <p className="text-2xl font-['SF_Pro_Display',_-apple-system,_sans-serif] font-bold text-purple-600 dark:text-purple-400">
                    {formatNumber(accepts)}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.3 }} className="mt-1">
          <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="post-observability-content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-8 pb-8 pt-4 border-t border-gray-100 dark:border-gray-800">
              <div className="max-h-[75vh] overflow-y-auto">
                <div className="sticky top-0 z-10 -mx-8 px-8 py-4 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800">
                  <div className="space-y-4">
                    {/* Controls */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {RANGE_PRESETS.map((p) => {
                          const isActive = range === p.value;
                          return (
                            <Button
                              key={p.value}
                              type="button"
                              size="sm"
                              variant={isActive ? 'default' : 'outline'}
                              aria-pressed={isActive}
                              aria-label={`Set range: ${p.label}`}
                              onClick={() => setRange(p.value)}
                              className={
                                isActive
                                  ? 'bg-emerald-600 hover:bg-emerald-700 border-emerald-600 text-white'
                                  : 'bg-white/80 dark:bg-gray-900/30'
                              }
                            >
                              {p.label}
                            </Button>
                          );
                        })}
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Bucket</span>
                          <Select value={bucket} onValueChange={(value) => setBucket(value as Bucket)}>
                            <SelectTrigger size="sm" className="w-[84px] bg-white/80 dark:bg-gray-900/30" aria-label="Bucket">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {BUCKETS.map((b) => (
                                <SelectItem key={b.value} value={b.value}>
                                  {b.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">Sort</span>
                          <Select value={sort} onValueChange={setSort}>
                            <SelectTrigger size="sm" className="w-[160px] bg-white/80 dark:bg-gray-900/30" aria-label="Sort posts">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {SORT_OPTIONS.map((o) => (
                                <SelectItem key={o.value} value={o.value}>
                                  {o.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>

                    {/* Search by Post ID */}
                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300">Post ID</div>
                        <div className="flex-1 flex items-center gap-2">
                          <Input
                            value={postIdSearch}
                            onChange={(e) => setPostIdSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSearchById();
                            }}
                            placeholder="e.g. 12345"
                            inputMode="numeric"
                            aria-label="Post ID search"
                            className="w-full sm:w-64"
                          />
                          <Button type="button" onClick={handleSearchById} className="bg-emerald-600 hover:bg-emerald-700">
                            Load
                          </Button>
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                        <div>{windowLabels.utc}</div>
                        <div>{windowLabels.et}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-6">
                  <Tabs
                    value={activeTab}
                    onValueChange={(value) => setActiveTab(value as ObservabilityTab)}
                    className="gap-4"
                  >
                <TabsList className="w-full sm:w-fit">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="waves">Waves</TabsTrigger>
                  <TabsTrigger value="posts">Posts</TabsTrigger>
                  <TabsTrigger value="detail">Detail</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-2">
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                      {overviewLoading ? (
                        <>
                          <Skeleton className="h-20 bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                          <Skeleton className="h-20 bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                          <Skeleton className="h-20 bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                          <Skeleton className="h-20 bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                          <Skeleton className="h-20 bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                          <Skeleton className="h-20 bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                        </>
                      ) : overview ? (
                        <>
                          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Waves</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(overview.metrics?.waves || 0)}</div>
                          </div>
                          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Avg EQ</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{(overview.metrics?.avgEq ?? 0).toFixed(2)}</div>
                          </div>
                          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">P95 Wave Duration</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {formatNumber(overview.metrics?.p95WaveDurationMs || 0)}ms
                            </div>
                          </div>
                          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Backfill Success</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {(((overview.metrics?.backfillSuccess ?? 0) as number) * 100).toFixed(0)}%
                            </div>
                          </div>
                          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Kafka Lag</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(overview.metrics?.kafkaLag || 0)}</div>
                          </div>
                          <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reads → Accepts</div>
                            <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                              {reads > 0 ? `${((accepts / reads) * 100).toFixed(1)}%` : '—'}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="col-span-full rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-4 text-sm text-amber-800 dark:text-amber-200">
                          {overviewError || 'No overview data.'}
                        </div>
                      )}
                    </div>

                    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Engagement Funnel</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Impressions → Reads → Replies / Accepts
                          </div>
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setActiveTab('detail')}>
                          Jump to detail
                        </Button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
                        <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Impressions</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(impressions)}</div>
                        </div>
                        <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Reads</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(reads)}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {impressions > 0 ? `${((reads / impressions) * 100).toFixed(1)}% of impressions` : '—'}
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Replies</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(replies)}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {reads > 0 ? `${((replies / reads) * 100).toFixed(1)}% of reads` : '—'}
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Accepts</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(accepts)}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {reads > 0 ? `${((accepts / reads) * 100).toFixed(1)}% of reads` : '—'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">EQ trend</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">By bucket</div>
                        <div className="h-60 mt-4">
                          {overviewLoading ? (
                            <Skeleton className="h-60 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                          ) : overview ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={overview.eq ?? []} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                                  stroke="#e5e7eb"
                                  interval="preserveStartEnd"
                                />
                                <YAxis
                                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                                  stroke="#e5e7eb"
                                />
                                <RechartsTooltip content={<MiniChartTooltip />} />
                                <Line type="monotone" dataKey="eq" name="EQ" stroke="#10b981" strokeWidth={2} dot={false} />
                              </LineChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="h-60 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                              No EQ data.
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Allocations</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Active vs revived</div>
                        <div className="h-60 mt-4">
                          {overviewLoading ? (
                            <Skeleton className="h-60 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                          ) : overview ? (
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={overview.allocations ?? []} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} vertical={false} />
                                <XAxis
                                  dataKey="label"
                                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                                  stroke="#e5e7eb"
                                  interval="preserveStartEnd"
                                />
                                <YAxis
                                  tick={{ fontSize: 11, fill: '#9ca3af', fontFamily: 'SF Pro Text, -apple-system, sans-serif' }}
                                  stroke="#e5e7eb"
                                />
                                <RechartsTooltip content={<MiniChartTooltip />} />
                                <Bar dataKey="active" name="Active" stackId="a" fill="#3b82f6" radius={[6, 6, 0, 0]} />
                                <Bar dataKey="revived" name="Revived" stackId="a" fill="#8b5cf6" radius={[6, 6, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <div className="h-60 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                              No allocation data.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-800/60 flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent waves</div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => setActiveTab('waves')}>
                          View all
                        </Button>
                      </div>

                      {overviewLoading ? (
                        <div className="p-4">
                          <Skeleton className="h-10 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                        </div>
                      ) : overview ? (
                        <Table className="text-sm">
                          <TableHeader className="bg-gray-50/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300">
                            <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                              <TableHead className="px-4 py-3 text-left font-semibold min-w-[180px]">Time</TableHead>
                              <TableHead className="px-4 py-3 text-right font-semibold">Duration</TableHead>
                              <TableHead className="px-4 py-3 text-right font-semibold">Active</TableHead>
                              <TableHead className="px-4 py-3 text-right font-semibold">Revived</TableHead>
                              <TableHead className="px-4 py-3 text-right font-semibold">EQ</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(overview.waves ?? []).slice(0, 5).length === 0 ? (
                              <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                                <TableCell colSpan={5} className="px-4 py-4 text-gray-500 dark:text-gray-400">
                                  No waves available for this window.
                                </TableCell>
                              </TableRow>
                            ) : (
                              (overview.waves ?? []).slice(0, 5).map((w) => (
                                <TableRow key={w.id} className="border-gray-200/60 dark:border-gray-800/60">
                                  <TableCell className="px-4 py-3 text-gray-900 dark:text-gray-100">{w.ts}</TableCell>
                                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.durationMs)}ms</TableCell>
                                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.active)}</TableCell>
                                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.revived)}</TableCell>
                                  <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{(w.eq ?? 0).toFixed(2)}</TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="p-4 text-sm text-gray-500 dark:text-gray-400">No wave data.</div>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="waves" className="mt-2">
                  <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-800/60 flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Waves</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {overviewLoading ? 'Loading…' : `${formatNumber(overview?.waves?.length ?? 0)} rows`}
                      </div>
                    </div>

                    {overviewLoading ? (
                      <div className="p-4">
                        <Skeleton className="h-10 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                      </div>
                    ) : overview ? (
                      <Table className="text-sm">
                        <TableHeader className="bg-gray-50/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300">
                          <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                            <TableHead className="px-4 py-3 text-left font-semibold min-w-[180px]">Time</TableHead>
                            <TableHead className="px-4 py-3 text-right font-semibold">Duration (ms)</TableHead>
                            <TableHead className="px-4 py-3 text-right font-semibold">Budget</TableHead>
                            <TableHead className="px-4 py-3 text-right font-semibold">Allocated</TableHead>
                            <TableHead className="px-4 py-3 text-right font-semibold">Active</TableHead>
                            <TableHead className="px-4 py-3 text-right font-semibold">Revived</TableHead>
                            <TableHead className="px-4 py-3 text-right font-semibold">EQ</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(overview.waves ?? []).length === 0 ? (
                            <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                              <TableCell colSpan={7} className="px-4 py-4 text-gray-500 dark:text-gray-400">
                                No waves available for this window.
                              </TableCell>
                            </TableRow>
                          ) : (
                            (overview.waves ?? []).map((w) => (
                              <TableRow key={w.id} className="border-gray-200/60 dark:border-gray-800/60">
                                <TableCell className="px-4 py-3 text-gray-900 dark:text-gray-100">{w.ts}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.durationMs)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.budget)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.allocated)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.active)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(w.revived)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{(w.eq ?? 0).toFixed(2)}</TableCell>
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      <div className="p-4 text-sm text-gray-500 dark:text-gray-400">No wave data.</div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="posts" className="mt-2">
                  <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 overflow-hidden">
                    <div className="px-4 py-3 border-b border-gray-200/60 dark:border-gray-800/60 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {postsTotal > 0 ? (
                          <span>
                            Showing <span className="font-semibold text-gray-900 dark:text-gray-100">{showingFrom}</span>–
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{showingTo}</span> of{' '}
                            <span className="font-semibold text-gray-900 dark:text-gray-100">{formatNumber(postsTotal)}</span>
                          </span>
                        ) : (
                          <span>No posts</span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">Page size</span>
                        <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                          <SelectTrigger size="sm" className="w-[84px] bg-white/80 dark:bg-gray-900/30" aria-label="Page size">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[10, 20, 50, 100].map((n) => (
                              <SelectItem key={n} value={String(n)}>
                                {n}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page <= 1}
                        >
                          Prev
                        </Button>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Page <span className="font-semibold text-gray-900 dark:text-gray-100">{page}</span> /{' '}
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{totalPages}</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                          disabled={page >= totalPages || postsTotal === 0}
                        >
                          Next
                        </Button>
                      </div>
                    </div>

                    <Table className="text-sm">
                      <TableHeader className="bg-gray-50/80 dark:bg-gray-800/60 text-gray-600 dark:text-gray-300">
                        <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                          <TableHead className="px-4 py-3 text-left font-semibold min-w-[320px]">Title</TableHead>
                          <TableHead className="px-4 py-3 text-left font-semibold min-w-[180px] whitespace-nowrap">
                            Created (ET)
                          </TableHead>
                          <TableHead className="px-4 py-3 text-left font-semibold">Author</TableHead>
                          <TableHead className="px-4 py-3 text-right font-semibold">EQ</TableHead>
                          <TableHead className="px-4 py-3 text-right font-semibold">Impressions</TableHead>
                          <TableHead className="px-4 py-3 text-right font-semibold">Replies</TableHead>
                          <TableHead className="px-4 py-3 text-right font-semibold">Accepts</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {postsLoading ? (
                          <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                            <TableCell colSpan={7} className="px-4 py-6">
                              <Skeleton className="h-10 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                            </TableCell>
                          </TableRow>
                        ) : postsError ? (
                          <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                            <TableCell colSpan={7} className="px-4 py-4 text-amber-700 dark:text-amber-300">
                              {postsError}
                            </TableCell>
                          </TableRow>
                        ) : posts.length === 0 ? (
                          <TableRow className="border-gray-200/60 dark:border-gray-800/60">
                            <TableCell colSpan={7} className="px-4 py-4 text-gray-500 dark:text-gray-400">
                              No posts available for this window.
                            </TableCell>
                          </TableRow>
                        ) : (
                          posts.map((p) => {
                            const isActive = p.id === selectedPostId;
                            return (
                              <TableRow
                                key={p.id}
                                data-state={isActive ? 'selected' : undefined}
                                className="border-gray-200/60 dark:border-gray-800/60"
                              >
                                <TableCell className="px-4 py-3 whitespace-normal">
                                  <button
                                    type="button"
                                    onClick={() => setSelectedPostId(p.id)}
                                    className="text-emerald-700 dark:text-emerald-300 hover:underline font-medium text-left"
                                  >
                                    {p.title}
                                  </button>
                                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2 whitespace-normal">{p.snippet}</div>
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                  {formatEtTimestamp(p.createdAt)}
                                </TableCell>
                                <TableCell className="px-4 py-3 text-gray-700 dark:text-gray-300">{p.authorName || p.authorId || '—'}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{(p.eq ?? 0).toFixed(2)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(p.impressions ?? 0)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(p.repliesCount ?? 0)}</TableCell>
                                <TableCell className="px-4 py-3 text-right text-gray-900 dark:text-gray-100">{formatNumber(p.accepts ?? 0)}</TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                <TabsContent value="detail" className="mt-2">
                  <div className="rounded-2xl border border-gray-200/60 dark:border-gray-800/60 bg-white/70 dark:bg-gray-900/40 p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Post Detail</div>
                        {selectedPostId ? <Badge variant="secondary">#{selectedPostId}</Badge> : null}
                        {detailLoading ? <span className="text-xs text-gray-500 dark:text-gray-400">Loading…</span> : null}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setActiveTab('posts')}>
                          Browse posts
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleCopySelectedPostId}
                          disabled={!selectedPostId}
                          aria-label="Copy selected Post ID"
                        >
                          Copy ID
                        </Button>
                        {copyState === 'copied' ? (
                          <span className="text-xs text-emerald-600 dark:text-emerald-400">Copied</span>
                        ) : copyState === 'failed' ? (
                          <span className="text-xs text-amber-700 dark:text-amber-300">Copy failed</span>
                        ) : null}
                      </div>
                    </div>

                    {detailError ? (
                      <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 text-sm text-amber-800 dark:text-amber-200 mb-3">
                        {detailError}
                      </div>
                    ) : null}

                    {detailLoading ? (
                      <Skeleton className="h-28 w-full bg-gray-200/60 dark:bg-gray-700/60 rounded-xl" />
                    ) : detail ? (
                      <div className="space-y-6">
                        <div>
                          <div className="text-lg font-bold text-gray-900 dark:text-gray-100">{detail.title}</div>
                          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed mt-2">{detail.content}</div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                          <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">EQ</div>
                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{(detail.eq ?? 0).toFixed(2)}</div>
                          </div>
                          <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Impressions</div>
                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(detail.impressions ?? 0)}</div>
                          </div>
                          <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Accepts</div>
                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(detail.accepts ?? 0)}</div>
                          </div>
                          <div className="rounded-xl border border-gray-200/60 dark:border-gray-800/60 bg-white/60 dark:bg-gray-900/30 p-3">
                            <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">Replies</div>
                            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{formatNumber(detail.repliesCount ?? 0)}</div>
                          </div>
                        </div>

                        <div className="lg:hidden">
                          <Tabs defaultValue="replies" className="gap-4">
                            <TabsList className="w-full sm:w-fit">
                              <TabsTrigger value="replies">Replies ({detail.replies?.length ?? 0})</TabsTrigger>
                              <TabsTrigger value="recipients">Recipients ({detail.users?.length ?? 0})</TabsTrigger>
                            </TabsList>
                            <TabsContent value="replies" className="mt-2">
                              {repliesSection}
                            </TabsContent>
                            <TabsContent value="recipients" className="mt-2">
                              {recipientsSection}
                            </TabsContent>
                          </Tabs>
                        </div>

                        <div className="hidden lg:grid lg:grid-cols-2 gap-4">
                          {repliesSection}
                          {recipientsSection}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        Select a post from the <span className="font-semibold text-gray-900 dark:text-gray-100">Posts</span> tab or search by Post ID above.
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

